import { useMemo } from 'react'
import { FinancialAccount } from '@/reducers/finances/financialAccountsReducer'
import { TypographyStyles } from '@/styles'
import { Badge, Tooltip, Text } from '@/ui'

import {
  hasAutoStatementUploadAccount,
  hasHealthyManualStatementUploadAccount,
} from '../../../helpers'

export const StatusPill = ({
  active,
  className,
}: {
  active: boolean
  className?: string
}) => (
  <Badge
    color={active ? 'green' : 'red'}
    variant="secondary"
    size="sm"
    className={className}
  >
    {active ? 'Active' : 'Disconnected'}
  </Badge>
)

export const StatementPill = ({
  accounts,
  className,
}: {
  accounts: FinancialAccount[]
  className?: string
}) => {
  const isAutomated = useMemo(
    () =>
      hasAutoStatementUploadAccount(accounts) ||
      hasHealthyManualStatementUploadAccount(accounts),
    [accounts]
  )

  return isAutomated ? (
    <Tooltip>
      <Tooltip.Trigger asChild>
        <Badge color="blue" variant="secondary" className={className} size="sm">
          Automated
        </Badge>
      </Tooltip.Trigger>

      <Tooltip.Content>
        <Text variant={TypographyStyles.H3} className="pt-2.5">
          Automated
        </Text>

        <Text>
          This means we can automatically reconcile and complete bookkeeping for
          this account. You don&apos;t need to upload statements every month.
        </Text>

        <Text className="mt-2.5">
          This includes accounts that were set up with automatic statement
          uploads and limited bank access.
        </Text>
      </Tooltip.Content>
    </Tooltip>
  ) : (
    <Tooltip>
      <Tooltip.Trigger asChild>
        <Badge color="pink" variant="secondary" size="sm" className={className}>
          Manual Upload
        </Badge>
      </Tooltip.Trigger>

      <Tooltip.Content>
        <Text variant={TypographyStyles.H3} className="pt-2.5">
          Manual Upload
        </Text>

        <Text>
          This means you need to manually upload bank statements every month for
          this account. This is required to complete bookkeeping.
        </Text>
      </Tooltip.Content>
    </Tooltip>
  )
}
